import React from "react";
import { Calendar, Clock, Video } from "lucide-react";
import BRAND_IMAGES from "@/lib/brand";
import EventConcludedNotice from "./EventConcludedNotice";

const Hero = ({ onRegister }) => {
  return (
    <section
      id="top"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 gradient-hero bokeh-bg overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Event concluded notice */}
        <EventConcludedNotice />

        {/* Top eyebrow */}
        <div className="text-center fade-up">
          <p className="eyebrow" data-testid="hero-series-label" style={{ fontSize: "0.85rem" }}>
            The Journey to Freedom · Next Masterclass
          </p>
        </div>

        {/* BIG headline at the very top */}
        <h1
          className="mt-5 md:mt-6 text-center font-serif leading-[1.02] tracking-tight balance fade-up delay-100"
          style={{
            color: "var(--ink)",
            fontSize: "clamp(2.5rem, 6vw, 5.25rem)",
            fontWeight: 500,
          }}
          data-testid="hero-headline"
        >
          Do Less.{" "}
          <em
            className="font-serif italic"
            style={{ color: "var(--gold-deep)", fontWeight: 500 }}
          >
            Be More.
          </em>
        </h1>

        <p
          className="mt-4 text-center font-serif italic fade-up delay-150 max-w-3xl mx-auto"
          style={{
            color: "var(--ink-soft)",
            fontSize: "clamp(1.3rem, 2.2vw, 1.9rem)",
            lineHeight: 1.35,
          }}
          data-testid="hero-tagline"
        >
          The freedom you seek isn't found in doing more. It's found in
          becoming more of who you truly are.
        </p>

        {/* Hero image */}
        <div className="mt-8 md:mt-10 fade-up delay-200">
          <div className="hero-image-frame max-w-2xl mx-auto">
            <img
              src={BRAND_IMAGES.heroLandscape}
              alt="Do Less. Be More. The Keys to Your Freedom, a masterclass with Persephone Dennis on Wednesday 18th June at 7pm BST."
              className="w-full h-auto block"
              data-testid="hero-banner-image"
              loading="eager"
            />
          </div>
        </div>

        {/* Register button immediately under the image */}
        <div className="mt-8 md:mt-10 flex flex-col items-center fade-up delay-300">
          <button
            onClick={onRegister}
            className="btn-primary text-base"
            data-testid="hero-image-register-btn"
            style={{ padding: "1.2rem 3rem", fontSize: "0.85rem" }}
          >
            Register Now
          </button>
          <p
            className="mt-3 eyebrow"
            style={{ fontSize: "0.75rem", letterSpacing: "0.35em" }}
          >
            Spaces are limited
          </p>
        </div>

        {/* Supporting copy, tight under the CTA */}
        <div className="mt-12 md:mt-16 max-w-3xl mx-auto text-center fade-up delay-400">
          <div
            className="space-y-6 leading-relaxed text-[color:var(--ink-soft)] pretty"
            style={{ fontSize: "1.2rem" }}
          >
            <p>
              We have been taught that more is the answer. More effort, more
              hustle, more achieving, more proving.
            </p>
            <p
              className="font-serif italic"
              style={{
                color: "var(--gold-deep)",
                fontSize: "1.75rem",
                lineHeight: 1.3,
              }}
            >
              But what if the doorway to freedom
              <br />
              <span style={{ color: "var(--ink)" }}>is doing less?</span>
            </p>
            <p>
              In this free live masterclass, we explore the quiet keys that
              unlock a more spacious, aligned and powerful way of being.
              Because true freedom is not found in doing more. It is found in
              becoming more of who you already are.
            </p>
          </div>
        </div>

        {/* Event details */}
        <div
          className="mt-14 md:mt-16 fade-up delay-500"
          data-testid="hero-event-details"
        >
          <div className="flex flex-col md:flex-row items-center justify-center gap-10 md:gap-20">
            <DetailItem
              icon={<Calendar strokeWidth={1.2} className="w-7 h-7" />}
              label="Date"
              value="Wednesday 18th June"
              testId="event-date"
            />
            <DetailItem
              icon={<Clock strokeWidth={1.2} className="w-7 h-7" />}
              label="Time"
              value="7pm BST"
              testId="event-time"
            />
            <DetailItem
              icon={<Video strokeWidth={1.2} className="w-7 h-7" />}
              label="Where"
              value="Live on Zoom"
              testId="event-location"
            />
          </div>
        </div>

        {/* Final hero CTA */}
        <div className="mt-12 md:mt-14 flex flex-col items-center fade-up delay-700">
          <button
            onClick={onRegister}
            className="btn-primary"
            data-testid="hero-register-btn"
            style={{ padding: "1.2rem 3rem", fontSize: "0.85rem" }}
          >
            Register Now
          </button>
        </div>
      </div>
    </section>
  );
};

const DetailItem = ({ icon, label, value, testId }) => (
  <div className="flex flex-col items-center text-center">
    <div className="text-[color:var(--gold-deep)] mb-3">{icon}</div>
    <span
      className="eyebrow mb-2"
      style={{ fontSize: "0.72rem", letterSpacing: "0.32em" }}
    >
      {label}
    </span>
    <span
      className="font-serif"
      style={{ color: "var(--ink)", fontSize: "1.6rem", lineHeight: 1.2 }}
      data-testid={testId}
    >
      {value}
    </span>
  </div>
);

export default Hero;
